import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from 'ag-grid-community';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useCallback, useMemo } from 'react';
import { fetchAccountsV2Promise } from 'reducers/accountsReducer';
import { getAccountLinksForSelectedUser } from 'reducers/usersReducer';
import { GetAccountsV2Response } from 'ts/api/accounts_v2';
import parseAGRequest from 'utils/agGrid/ssrm/parseAGRequest';
import { ServerSideFilter } from 'utils/agGrid/types';
import { CLEARING } from 'constants/accountTypes';
import BalanceGridRow from '../types';
import { AccountLink } from 'ts/models/User.model';

const useDatasource = () => {
  const dispatch = useAppDispatch();

  const accountLinks: AccountLink[] = useAppSelector(
    getAccountLinksForSelectedUser,
  );

  const userAccounts = useMemo(
    () => accountLinks?.map((link) => link.accountId),
    [accountLinks],
  );

  const getAccounts = useCallback(
    async (req: IServerSideGetRowsRequest) => {
      const body = parseAGRequest(req, { transformAttrs: true });
      let filter: ServerSideFilter[] = [];

      if (!userAccounts) {
        filter = filter.concat([
          { attr: 'account_type', op: 'eq', value: CLEARING },
        ]);
      } else {
        filter = filter.concat([
          { attr: 'account_id', value: userAccounts, op: 'eq' },
        ]);
      }

      const result: GetAccountsV2Response = await fetchAccountsV2Promise(
        {
          ...body,
          filter: [...filter, ...(body?.filter ? body.filter : [])],
          showUsers: true,
        },
        dispatch,
      );
      return result;
    },
    [dispatch, userAccounts],
  );

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async ({
        request,
        success,
      }: IServerSideGetRowsParams<BalanceGridRow>) => {
        const accounts = await getAccounts(request);
        success({
          rowData: accounts.accounts ?? [],
          rowCount: accounts.count ?? undefined,
        });
      },
    }),
    [getAccounts],
  );
  return datasource;
};

export default useDatasource;
